import React from "react";
import { Box, Paper, Typography, TextField, Button, Chip } from "@mui/material";
import globals from "../../Globals";
import { PageContainer, SectionHeader, SelectorBar, SelectField, DataTable } from "../../Layout/Common";
import { FIXED_TABLE } from "../Localization/loc";
import { chat, savedBranch, saveBranch, canEdit, date, plainChannel, REPORT_REASONS, PlayerLink, ConfirmAction } from "./community";

// Community → Chat reports (service `chat`, Docs/Social.md §6 + §10): the report queue of a branch (`chat reports <branch> open|all`)
// with each reported line's text (`chat lines <channel> <seq - 1>`, one read per report), resolve hide | ok; the Lines view of
// a channel (`chat lines <channel> [afterSeq]`, hidden lines flagged, hide / unhide); a player's mute / unmute. `#chat` views,
// `chat` acts.
const lineText = (l) => (!l ? "" : l.kind === 2 ? `[${l.tag}${l.args?.length ? " " + l.args.join(", ") : ""}]` : l.text);

export default class ChatTab extends React.Component {
    state = {
        branch: savedBranch(), which: "open", reports: null, texts: {}, loading: false, busy: false, action: null,
        channel: "", afterSeq: "", lines: null, linesChannel: "",
        player: "", hours: "24", reason: "",
    };

    componentDidMount() { this.load(); }

    load = () => {
        const { branch, which } = this.state;
        saveBranch(branch);
        this.setState({ loading: true });
        chat("Loading the reports", ["reports", branch, which], { silent: true })
            .then(r => { const reports = r?.reports ?? []; this.setState({ reports }); this.fillTexts(reports); })
            .catch(() => this.setState({ reports: [] }))
            .finally(() => this.setState({ loading: false }));
    }

    // The reported lines' texts, one read each (the queue carries the channel + seq only), in turn
    fillTexts = async (reports) => {
        for (const rep of reports) {
            const key = `${rep.ch}#${rep.seq}`;
            if (this.state.texts[key] !== undefined) continue;
            try {
                const r = await chat("Loading a reported line", ["lines", plainChannel(rep.ch), String(Math.max(0, rep.seq - 1)), "--branch", this.state.branch], { silent: true });
                const line = (r?.lines ?? []).find(l => l.seq === rep.seq);
                this.setState(s => ({ texts: { ...s.texts, [key]: line ?? null } }));
            } catch { this.setState(s => ({ texts: { ...s.texts, [key]: null } })); }
        }
    }

    loadLines = (channel = this.state.channel, afterSeq = this.state.afterSeq) => {
        const ch = String(channel).trim();
        if (!ch) return;
        this.setState({ channel: ch, afterSeq: String(afterSeq ?? "") });
        chat("Loading the lines", ["lines", ch, ...(String(afterSeq ?? "").trim() ? [String(afterSeq).trim()] : []), "--branch", this.state.branch], { silent: true })
            .then(r => this.setState({ lines: r?.lines ?? [], linesChannel: ch }))
            .catch(() => this.setState({ lines: [], linesChannel: ch }));
    }

    act = (parts, description, success) => {
        this.setState({ busy: true });
        return chat(description, parts, { successMessage: success })
            .then(() => { this.setState({ action: null, texts: {} }); this.load(); if (this.state.linesChannel) this.loadLines(this.state.linesChannel, this.state.afterSeq); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    }

    resolve = (rep, how) => this.setState({ action: {
        title: how === "hide" ? `Hide ${plainChannel(rep.ch)}#${rep.seq}?` : `Keep ${plainChannel(rep.ch)}#${rep.seq}?`,
        confirmText: how === "hide" ? "Hide the line" : "Mark ok",
        text: how === "hide" ? "The line disappears for every player (Poll leaves it out); every open report of it is resolved hidden." : "The line stays; every open report of it is resolved ok.",
        run: () => this.act(["resolve", rep.id, how], "Resolving the report", "Report resolved"),
    }});

    toggleLine = (l) => this.setState({ action: {
        title: `${l.hidden ? "Unhide" : "Hide"} ${this.state.linesChannel}#${l.seq}?`, confirmText: l.hidden ? "Unhide" : "Hide",
        text: l.hidden ? "The line is visible again to the channel's readers." : "The line disappears for every player (Poll leaves it out).",
        run: () => this.act([l.hidden ? "unhide" : "hide", this.state.linesChannel, String(l.seq), "--branch", this.state.branch], l.hidden ? "Unhiding the line" : "Hiding the line", "Done"),
    }});

    mute = (unmute) => {
        const { player, hours, reason } = this.state;
        if (!player.trim()) return;
        if (!unmute && !(Number(hours) > 0)) { window.alert("Hours must be a positive number."); return; }
        this.setState({ action: unmute ? {
            title: `Unmute ${player.trim()}?`, confirmText: "Unmute", text: "The player may send again at once.",
            run: () => this.act(["unmute", player.trim()], "Unmuting", "Unmuted"),
        } : {
            title: `Mute ${player.trim()} for ${hours} h?`, confirmText: "Mute",
            text: `Every Send answers Muted until then${reason.trim() ? ` (reason: ${reason.trim()})` : ""}.`,
            run: () => this.act(["mute", player.trim(), String(hours), ...(reason.trim() ? [reason.trim()] : [])], "Muting", "Muted"),
        }});
    }

    render() {
        const { branch, which, reports, texts, loading, busy, action, channel, afterSeq, lines, linesChannel, player, hours, reason } = this.state;
        const edit = canEdit("chat");
        const reportColumns = [
            { id: "text", label: "Line", width: "24%", render: r => {
                const key = `${r.ch}#${r.seq}`, line = texts[key];
                return line === undefined ? <Typography variant="caption" color="text.secondary">…</Typography>
                    : line === null ? <Typography variant="caption" color="text.secondary">(gone: older than 30 days)</Typography>
                    : <span style={{ wordBreak: "break-word" }}>{line.name ? <b>{line.name}: </b> : null}{lineText(line)}{line.hidden ? <Chip size="small" label="hidden" sx={{ ml: 1 }} /> : null}</span>;
            } },
            { id: "channel", label: "Channel", width: "16%", render: r => (
                <Typography component="span" variant="body2" sx={{ color: "primary.main", cursor: "pointer", wordBreak: "break-all", fontFamily: "monospace", fontSize: 12 }}
                            onClick={() => this.loadLines(plainChannel(r.ch), Math.max(0, r.seq - 10))}>{plainChannel(r.ch)}#{r.seq}</Typography>) },
            { id: "by", label: "Reporter", width: "10%", render: r => <PlayerLink id={r.by} /> },
            { id: "reason", label: "Reason", width: "7%", render: r => REPORT_REASONS[r.reason] ?? r.reason },
            { id: "note", label: "Note", width: "13%", render: r => <span style={{ wordBreak: "break-word" }}>{r.note}</span> },
            { id: "at", label: "At", width: "11%", render: r => date(r.at) },
            { id: "state", label: "State", width: "7%", render: r => <span title={r.resolvedBy ? `by ${r.resolvedBy} ${date(r.resolvedAt)}` : ""}>{r.state}</span> },
            { id: "actions", label: "", width: "12%", align: "right", render: r => edit && r.state === "open" ? (
                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                    <Button size="small" color="error" onClick={() => this.resolve(r, "hide")} disabled={busy}>Hide</Button>
                    <Button size="small" onClick={() => this.resolve(r, "ok")} disabled={busy}>Ok</Button>
                </Box>) : "" },
        ];
        const lineColumns = [
            { id: "seq", label: "#", width: "7%", align: "right" },
            { id: "from", label: "From", width: "20%", render: l => l.from ? <PlayerLink id={l.from} name={l.name} /> : (l.name || "system") },
            { id: "text", label: "Text", width: "43%", render: l => <span style={{ wordBreak: "break-word", opacity: l.hidden ? 0.5 : 1 }}>{lineText(l)}{l.hidden ? <Chip size="small" label="hidden" sx={{ ml: 1 }} /> : null}</span> },
            { id: "at", label: "At", width: "18%", render: l => date(l.at) },
            { id: "actions", label: "", width: "12%", align: "right", render: l => edit ? <Button size="small" color={l.hidden ? "primary" : "error"} onClick={() => this.toggleLine(l)} disabled={busy}>{l.hidden ? "Unhide" : "Hide"}</Button> : "" },
        ];
        return (
            <PageContainer>
                <SectionHeader title="Chat reports">
                    <Typography variant="body2" color="text.secondary">{globals.selectedTitle} · reported lines wait here; nothing is checked by AI per line</Typography>
                </SectionHeader>
                <SelectorBar>
                    <SelectField label="Branch" size="small" minWidth={160} value={branch} options={globals.branches} onChange={v => this.setState({ branch: v, texts: {} }, this.load)} />
                    <SelectField label="Reports" size="small" minWidth={140} value={which} options={[{ value: "open", label: "Open" }, { value: "all", label: "All" }]} onChange={v => this.setState({ which: v }, this.load)} />
                    <Button variant="outlined" onClick={this.load} disabled={loading}>{loading ? "Loading…" : "Reload"}</Button>
                </SelectorBar>
                {reports && reports.length > 0
                    ? <DataTable sx={FIXED_TABLE} columns={reportColumns} rows={reports} getRowKey={r => r.id} />
                    : <Paper sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{loading || !reports ? "Loading…" : `No ${which === "open" ? "open " : ""}report on this branch.`}</Typography></Paper>}

                <Paper sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Lines</Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <TextField label="Channel (dm:<a>:<b> or guild:<id>)" size="small" value={channel} onChange={e => this.setState({ channel: e.target.value })} onKeyDown={e => { if (e.key === "Enter") this.loadLines(); }} sx={{ width: 420 }} />
                        <TextField label="After seq" size="small" value={afterSeq} onChange={e => this.setState({ afterSeq: e.target.value.replace(/[^0-9]/g, "") })} sx={{ width: 120 }} />
                        <Button variant="outlined" onClick={() => this.loadLines()}>Load</Button>
                        {lines && lines.length > 0 && <Button onClick={() => this.loadLines(linesChannel, lines[lines.length - 1].seq)}>Next 50</Button>}
                    </Box>
                    {lines && (lines.length > 0
                        ? <Box sx={{ mt: 2 }}><DataTable sx={FIXED_TABLE} columns={lineColumns} rows={lines} getRowKey={l => l.seq} /></Box>
                        : <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No line in {linesChannel} after that seq.</Typography>)}
                </Paper>

                {edit && (
                    <Paper sx={{ p: 2, mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Mute a player</Typography>
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                            <TextField label="Player (link or id)" size="small" value={player} onChange={e => this.setState({ player: e.target.value })} sx={{ width: 260 }} />
                            <TextField label="Hours" size="small" value={hours} onChange={e => this.setState({ hours: e.target.value.replace(/[^0-9.]/g, "") })} sx={{ width: 100 }} />
                            <TextField label="Reason" size="small" value={reason} onChange={e => this.setState({ reason: e.target.value })} sx={{ width: 280 }} />
                            <Button variant="contained" color="warning" onClick={() => this.mute(false)} disabled={busy || !player.trim()}>Mute</Button>
                            <Button variant="outlined" onClick={() => this.mute(true)} disabled={busy || !player.trim()}>Unmute</Button>
                        </Box>
                    </Paper>
                )}
                <ConfirmAction action={action} busy={busy} onClose={() => this.setState({ action: null })} />
            </PageContainer>
        );
    }
}
